// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Motion-triggered clip recording. A fresh MediaRecorder is created per event so
 * every clip is a self-contained, immediately-playable file. Encoding happens in
 * the browser's own recorder pipeline — the bytes never leave the device.
 */

/** Pick the best-supported clip container/codec this browser can encode. */
export function pickClipMime(): string {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ];
  const MR = (window as unknown as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return '';
  for (const c of candidates) {
    if (MR.isTypeSupported(c)) return c;
  }
  return '';
}

export function recordingSupported(): boolean {
  return typeof (window as unknown as { MediaRecorder?: unknown }).MediaRecorder === 'function';
}

/** A single in-flight clip recording, resolved to a Blob when stopped. */
export class ClipRecorder {
  private rec: MediaRecorder;
  private chunks: BlobData[] = [];
  private done: Promise<Blob>;
  readonly mime: string;

  constructor(stream: MediaStream, mime: string) {
    this.mime = mime;
    this.rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    this.done = new Promise<Blob>((resolve) => {
      this.rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      this.rec.onstop = () => {
        resolve(new Blob(this.chunks, { type: this.mime || 'video/webm' }));
      };
    });
    this.rec.start(250); // periodic chunks keep memory bounded
  }

  stop(): Promise<Blob> {
    if (this.rec.state !== 'inactive') this.rec.stop();
    return this.done;
  }
}

type BlobData = Blob;
